/**
 * RUT Formatter & Validator v2.6
 *
 * Compatible con WooCommerce Blocks (React)
 * Usa técnica de native setter para sincronizar con React state
 * v2.6: Company RUT confirmation modal - simplified bypass using sessionStorage
 */

(function() {
    'use strict';

    const DEBUG = window.location.search.includes('spcb_debug=1');
    const log = (...args) => DEBUG && console.log('[SPCB RUT v2.6]', ...args);

    // Log version to confirm correct file is loaded
    console.log('[SPCB RUT] Version 2.6 loaded');
    const isCheckoutProcessing = () => {
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            return true;
        }

        const checkoutStore = window.wp?.data?.select?.('wc/store/checkout');
        return !!(
            checkoutStore?.isProcessing?.() ||
            checkoutStore?.isBeforeProcessing?.() ||
            checkoutStore?.isAfterProcessing?.()
        );
    };

    const timers = (() => {
        const timeouts = new Map();
        const pausedTimeouts = [];
        let paused = false;

        const scheduleTimeout = (fn, delay, label) => {
            if (paused || isCheckoutProcessing()) {
                log('Timeout blocked - checkout processing:', label);
                return null;
            }

            const id = setTimeout(() => {
                timeouts.delete(id);

                if (paused || isCheckoutProcessing()) {
                    log('Timeout skipped - checkout processing:', label);
                    return;
                }

                fn();
            }, delay);

            timeouts.set(id, { fn, delay, label });
            return id;
        };

        const pause = () => {
            if (paused) return;
            paused = true;

            timeouts.forEach((meta, id) => {
                clearTimeout(id);
                pausedTimeouts.push(meta);
            });
            timeouts.clear();

            log('Timers paused - checkout processing');
        };

        const resume = () => {
            if (!paused) return;
            paused = false;

            while (pausedTimeouts.length) {
                const meta = pausedTimeouts.shift();
                if (meta?.fn) {
                    scheduleTimeout(meta.fn, 0, meta.label);
                }
            }

            log('Timers resumed - checkout processing ended');
        };

        return {
            scheduleTimeout,
            pause,
            resume
        };
    })();

    if (typeof window.spcbCheckoutLock?.subscribe === 'function') {
        window.spcbCheckoutLock.subscribe((processing) => {
            if (processing) {
                timers.pause();
            } else {
                timers.resume();
            }
        });
    }

    /**
     * Clean RUT - remove all non-alphanumeric except K
     */
    function cleanRut(rut) {
        if (!rut) return '';
        return rut.replace(/[^0-9kK]/g, '').toUpperCase();
    }

    /**
     * Format RUT with dots and dash (12.345.678-9)
     */
    function formatRut(rut) {
        const cleaned = cleanRut(rut);
        if (cleaned.length < 2) return cleaned;

        const verifier = cleaned.slice(-1);
        const number = cleaned.slice(0, -1);

        // Add thousand separators
        const formatted = number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

        return formatted + '-' + verifier;
    }

    /**
     * Calculate verification digit using Module 11
     */
    function calculateVerifier(number) {
        let sum = 0;
        let multiplier = 2;

        for (let i = number.length - 1; i >= 0; i--) {
            sum += parseInt(number[i], 10) * multiplier;
            multiplier = multiplier === 7 ? 2 : multiplier + 1;
        }

        const remainder = 11 - (sum % 11);

        if (remainder === 11) return '0';
        if (remainder === 10) return 'K';
        return String(remainder);
    }

    /**
     * Validate RUT
     */
    function isValidRut(rut) {
        const cleaned = cleanRut(rut);

        if (cleaned.length < 8 || cleaned.length > 9) {
            return false;
        }

        const verifier = cleaned.slice(-1);
        const number = cleaned.slice(0, -1);

        if (!/^\d+$/.test(number)) {
            return false;
        }

        return calculateVerifier(number) === verifier;
    }

    /**
     * Check if RUT is likely a company (>= threshold)
     */
    function isCompanyRut(rut, threshold = 50000000) {
        const cleaned = cleanRut(rut);
        const number = parseInt(cleaned.slice(0, -1), 10);
        return number >= threshold;
    }

    /**
     * Set value in React-controlled input
     * Critical for WooCommerce Blocks compatibility
     */
    function setReactInputValue(input, value) {
        const helper = window.spcbCheckoutLock?.safeSetReactInputValue;
        if (typeof helper === 'function') {
            const updated = helper(input, value, 'rut-formatter');
            if (updated) {
                log('setReactInputValue:', input.id, value);
            }
            return;
        }

        if (!input) {
            log('setReactInputValue blocked - missing input');
            return;
        }

        if (isCheckoutProcessing()) {
            log('setReactInputValue blocked - checkout processing:', input.id);
            return;
        }

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
        ).set;
        nativeInputValueSetter.call(input, value);

        // Dispatch input event for React
        const inputEvent = new InputEvent('input', {
            bubbles: true,
            cancelable: true,
            inputType: 'insertText',
            data: value,
        });
        input.dispatchEvent(inputEvent);

        // Dispatch change event
        const changeEvent = new Event('change', { bubbles: true, cancelable: true });
        input.dispatchEvent(changeEvent);

        log('setReactInputValue:', input.id, value);
    }

    /**
     * Initialize RUT field handling
     */
    function initRutField(input) {
        if (!input || input.dataset.spcbRutInitialized) return;

        input.dataset.spcbRutInitialized = 'true';
        log('Initializing RUT field:', input.id);

        // Format existing value on load (with small delay for React to settle)
        timers.scheduleTimeout(() => {
            const currentValue = input.value;
            const cleaned = cleanRut(currentValue);
            if (cleaned.length >= 2) {
                const formatted = formatRut(cleaned);
                if (formatted !== currentValue) {
                    setReactInputValue(input, formatted);
                    log('Formatted on init:', formatted);
                }
            }
        }, 100, 'rut-format-init');

        // Format on blur (when leaving the field)
        input.addEventListener('blur', function(e) {
            const cleaned = cleanRut(e.target.value);
            if (cleaned.length >= 2) {
                const formatted = formatRut(cleaned);
                // Use React-compatible setter
                setReactInputValue(input, formatted);
                log('Formatted on blur:', formatted);
            }
            updateValidationState(input);
        });

        // Paste handling
        input.addEventListener('paste', function(e) {
            e.preventDefault();
            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
            const cleaned = cleanRut(pastedText);
            const formatted = formatRut(cleaned);
            setReactInputValue(input, formatted);
            updateValidationState(input);
        });

        // v1.10.31: Initialize help text and observer
        const fieldType = input.dataset.spcbField;
        if (fieldType === 'billing_rut' || fieldType === 'shipping_rut') {
            // Add help text after a small delay (to ensure DOM is ready)
            timers.scheduleTimeout(() => {
                updateRutHelpText(input, fieldType);
                observeRutFieldValidation(input);
            }, 200, 'rut-helptext');
        }

        log('RUT field initialized:', input.id);
    }

    /**
     * Update validation state classes
     */
    function updateValidationState(input) {
        const wrapper = input.closest('.wc-block-components-text-input') || input.parentElement;
        const cleaned = cleanRut(input.value);

        wrapper.classList.remove('spcb-valid', 'spcb-invalid');

        if (cleaned.length >= 8) {
            if (isValidRut(cleaned)) {
                wrapper.classList.add('spcb-valid');
            } else {
                wrapper.classList.add('spcb-invalid');
            }
        }

        // Update help text visibility based on validation state
        const fieldType = input.dataset.spcbField;
        if (fieldType === 'billing_rut' || fieldType === 'shipping_rut') {
            updateRutHelpText(input, fieldType);
        }
    }

    // ============================================
    // RUT HELP TEXT (v1.10.31)
    // Textos explicativos debajo de los campos RUT
    // ============================================

    /**
     * Get current document type (boleta/factura)
     * Reads from body class (most reliable) with fallback to select
     */
    function getDocumentType() {
        // Método más confiable: leer de la clase del body
        if (document.body.classList.contains('spcb-factura-mode')) {
            return 'factura';
        }
        if (document.body.classList.contains('spcb-boleta-mode')) {
            return 'boleta';
        }
        // Fallback: leer del select oculto
        const select = document.querySelector('[data-spcb-field="document_type"] select');
        return select?.value || 'boleta';
    }

    /**
     * Create or update RUT help text below the field
     * @param {HTMLInputElement} input - The RUT input field
     * @param {string} fieldType - 'billing_rut' or 'shipping_rut'
     * @param {string|null} forcedDocumentType - Optional: pass document type directly to avoid timing issues
     */
    function updateRutHelpText(input, fieldType, forcedDocumentType = null) {
        const wrapper = input.closest('.wc-block-components-text-input');
        if (!wrapper) return;

        let helpDiv = wrapper.querySelector('.spcb-rut-help-text');

        // Si hay error de validación activo, ocultar ayuda
        const hasError = wrapper.classList.contains('has-error');
        if (hasError) {
            if (helpDiv) helpDiv.style.display = 'none';
            return;
        }

        // Crear div si no existe
        if (!helpDiv) {
            helpDiv = document.createElement('div');
            helpDiv.className = 'spcb-rut-help-text';
            wrapper.appendChild(helpDiv);
        }

        helpDiv.style.display = '';

        // Determinar el texto según el campo y tipo de documento
        // Usar valor forzado si se proporciona (evita problemas de timing)
        const documentType = forcedDocumentType || getDocumentType();

        if (fieldType === 'billing_rut') {
            helpDiv.textContent = documentType === 'factura'
                ? 'RUT utilizado para la generación de la factura'
                : 'RUT usado para la boleta electrónica';
        } else if (fieldType === 'shipping_rut') {
            helpDiv.textContent = 'RUT de la persona que recibirá el envío';
        }

        log('Updated help text for', fieldType, '- docType:', documentType, '- text:', helpDiv.textContent);
    }

    /**
     * Observe validation error changes on RUT fields
     * When has-error class is added/removed, update help text visibility
     */
    function observeRutFieldValidation(input) {
        const wrapper = input.closest('.wc-block-components-text-input');
        if (!wrapper || wrapper._spcbHelpTextObserver) return;

        const fieldType = input.dataset.spcbField;
        if (fieldType !== 'billing_rut' && fieldType !== 'shipping_rut') return;

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class') {
                    updateRutHelpText(input, fieldType);
                }
            });
        });

        observer.observe(wrapper, { attributes: true, attributeFilter: ['class'] });
        wrapper._spcbHelpTextObserver = observer;

        log('Help text observer attached to', fieldType);
    }

    /**
     * Initialize all RUT fields
     */
    function initAllRutFields() {
        const rutFields = document.querySelectorAll(
            'input[data-spcb-field="billing_rut"], ' +
            'input[data-spcb-field="shipping_rut"]'
        );

        rutFields.forEach(initRutField);
        log('Found', rutFields.length, 'RUT fields');
    }

    /**
     * Watch for dynamically added fields
     */
    function observeNewFields() {
        const observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                mutation.addedNodes.forEach(function(node) {
                    if (node.nodeType !== Node.ELEMENT_NODE) return;

                    if (node.querySelectorAll) {
                        const rutFields = node.querySelectorAll(
                            'input[data-spcb-field*="rut"]'
                        );
                        rutFields.forEach(initRutField);
                    }

                    if (node.matches && node.matches('input[data-spcb-field*="rut"]')) {
                        initRutField(node);
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    // Initialize
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            initAllRutFields();
            observeNewFields();
        });
    } else {
        initAllRutFields();
        observeNewFields();
    }

    // Retry initialization (fields may appear later)
    [500, 1000, 2000].forEach(delay => {
        timers.scheduleTimeout(initAllRutFields, delay, 'rut-init-retry');
    });

    // Expose utilities globally
    window.spcbRut = {
        clean: cleanRut,
        format: formatRut,
        validate: isValidRut,
        isCompany: isCompanyRut,
        calculateVerifier: calculateVerifier
    };

    // v1.10.31: Expose help text updater for checkout-document.js
    window.spcbUpdateRutHelpText = updateRutHelpText;

    /**
     * Mostrar modal de validación
     */
    function showValidationModal(fieldName, message, input) {
        // Remover modal existente si hay
        const existingModal = document.querySelector('.spcb-validation-modal-overlay');
        if (existingModal) existingModal.remove();

        // Crear overlay
        const overlay = document.createElement('div');
        overlay.className = 'spcb-validation-modal-overlay';
        overlay.innerHTML = `
            <div class="spcb-validation-modal">
                <div class="spcb-validation-modal-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="48" height="48">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z">
                        </path>
                    </svg>
                </div>
                <h3 class="spcb-validation-modal-title">Campo requerido</h3>
                <p class="spcb-validation-modal-field">${fieldName}</p>
                <p class="spcb-validation-modal-message">${message}</p>
                <button type="button" class="spcb-validation-modal-button">
                    Ir al campo
                </button>
            </div>
        `;

        // Agregar estilos inline si no existen
        if (!document.querySelector('#spcb-validation-modal-styles')) {
            const styles = document.createElement('style');
            styles.id = 'spcb-validation-modal-styles';
            styles.textContent = `
                .spcb-validation-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 999999;
                    animation: spcb-fade-in 0.2s ease;
                }
                @keyframes spcb-fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .spcb-validation-modal {
                    background: white;
                    border-radius: 12px;
                    padding: 32px;
                    max-width: 400px;
                    width: 90%;
                    text-align: center;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                    animation: spcb-slide-up 0.3s ease;
                }
                @keyframes spcb-slide-up {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .spcb-validation-modal-icon {
                    color: #dc2626;
                    margin-bottom: 16px;
                }
                .spcb-validation-modal-title {
                    margin: 0 0 8px 0;
                    font-size: 20px;
                    font-weight: 600;
                    color: #1f2937;
                }
                .spcb-validation-modal-field {
                    margin: 0 0 8px 0;
                    font-size: 16px;
                    font-weight: 500;
                    color: #dc2626;
                }
                .spcb-validation-modal-message {
                    margin: 0 0 24px 0;
                    font-size: 14px;
                    color: #6b7280;
                }
                .spcb-validation-modal-button {
                    background: #008060;
                    color: white;
                    border: none;
                    padding: 12px 32px;
                    border-radius: 8px;
                    font-size: 16px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .spcb-validation-modal-button:hover {
                    background: #006e52;
                }
            `;
            document.head.appendChild(styles);
        }

        document.body.appendChild(overlay);

        // Manejar click en botón
        const button = overlay.querySelector('.spcb-validation-modal-button');
        button.addEventListener('click', function() {
            overlay.remove();
            goToField(input);
        });

        // Cerrar con click en overlay
        overlay.addEventListener('click', function(e) {
            if (e.target === overlay) {
                overlay.remove();
                goToField(input);
            }
        });

        // Cerrar con Escape
        function handleEscape(e) {
            if (e.key === 'Escape') {
                overlay.remove();
                goToField(input);
                document.removeEventListener('keydown', handleEscape);
            }
        }
        document.addEventListener('keydown', handleEscape);
    }

    /**
     * Ir al campo - expande sección si está oculta y hace scroll
     */
    function goToField(input) {
        const wrapper = input.closest('.wc-block-components-text-input');

        // Buscar si hay un botón "Editar" en la sección de envío
        const shippingSection = input.closest('.wp-block-woocommerce-checkout-shipping-address-block');
        if (shippingSection) {
            const editButton = shippingSection.querySelector('button[aria-expanded="false"]');
            if (editButton) {
                editButton.click();
                log('Clicked Edit button to expand section');
            }
        }

        // Pequeño delay para que la sección se expanda
        timers.scheduleTimeout(() => {
            // Agregar clase de error
            if (wrapper) {
                wrapper.classList.add('has-error');
                input.setAttribute('aria-invalid', 'true');

                // Agregar mensaje de error debajo del campo
                let errorDiv = wrapper.querySelector('.wc-block-components-validation-error');
                if (!errorDiv) {
                    errorDiv = document.createElement('div');
                    errorDiv.className = 'wc-block-components-validation-error';
                    errorDiv.setAttribute('role', 'alert');
                    wrapper.appendChild(errorDiv);
                }
                errorDiv.textContent = 'Este campo es obligatorio para Factura';
                errorDiv.style.display = 'block';
            }

            // Scroll y focus
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
            timers.scheduleTimeout(() => input.focus(), 300, 'rut-focus');
        }, 100, 'rut-go-to-field');
    }

    /**
     * Limpiar error de validación
     */
    function clearFieldError(input) {
        const wrapper = input.closest('.wc-block-components-text-input');
        if (!wrapper) return;

        wrapper.classList.remove('has-error');
        input.setAttribute('aria-invalid', 'false');

        const errorDiv = wrapper.querySelector('.wc-block-components-validation-error');
        if (errorDiv) {
            errorDiv.style.display = 'none';
        }
    }

    /**
     * CONFIRMACIÓN RUT EMPRESA + BOLETA (v2.6)
     * Muestra modal de confirmación cuando se detecta RUT de empresa con Boleta seleccionada
     * Usa sessionStorage para el bypass (más confiable que variable en memoria)
     */
    const BYPASS_KEY = 'spcb_company_confirmation_bypass';

    function setBypass(value) {
        if (value) {
            sessionStorage.setItem(BYPASS_KEY, 'true');
            log('Bypass SET');
        } else {
            sessionStorage.removeItem(BYPASS_KEY);
            log('Bypass CLEARED');
        }
    }

    function getBypass() {
        return sessionStorage.getItem(BYPASS_KEY) === 'true';
    }

    /**
     * Leer el tipo de documento DIRECTAMENTE del DOM (fuente de verdad)
     */
    function getDocumentTypeFromDOM() {
        // 1. Leer del radio checked (más confiable - lo que el usuario VE)
        const checkedRadio = document.querySelector(
            'input[name="spcb-document-type-radio"]:checked, ' +
            'input[name="spcb-document-type"]:checked'
        );
        if (checkedRadio) {
            log('getDocumentTypeFromDOM: from radio =', checkedRadio.value);
            return checkedRadio.value;
        }

        // 2. Fallback: leer del select
        const select = document.querySelector(
            'select[data-spcb-field="document_type"], ' +
            '#contact-spcb-document_type'
        );
        if (select && select.value) {
            log('getDocumentTypeFromDOM: from select =', select.value);
            return select.value;
        }

        // 3. Fallback final: body class
        if (document.body.classList.contains('spcb-factura-mode')) {
            log('getDocumentTypeFromDOM: from body class = factura');
            return 'factura';
        }

        log('getDocumentTypeFromDOM: default = boleta');
        return 'boleta';
    }

    function needsCompanyConfirmation() {
        log('needsCompanyConfirmation: checking...');

        // v1.17.8: Verificar si el modal está habilitado en la configuración
        const modalEnabled = window.spcbConfig?.companyRutConfirmationModal !== false;
        if (!modalEnabled) {
            log('needsCompanyConfirmation: MODAL DISABLED in settings');
            return false;
        }

        // Si ya confirmó en esta sesión, no volver a preguntar
        if (getBypass()) {
            log('needsCompanyConfirmation: BYPASS ACTIVE, skipping');
            return false;
        }

        // Obtener tipo de documento DIRECTAMENTE del DOM
        const currentDocType = getDocumentTypeFromDOM();

        // Si ya es Factura, no necesita confirmación
        if (currentDocType === 'factura') {
            log('needsCompanyConfirmation: already Factura, no confirmation needed');
            return false;
        }

        // Obtener RUT de billing
        const billingRutInput = document.querySelector('input[data-spcb-field="billing_rut"]');
        if (!billingRutInput) {
            log('needsCompanyConfirmation: no billing_rut input found');
            return false;
        }

        const rutValue = cleanRut(billingRutInput.value);
        log('needsCompanyConfirmation: RUT value =', rutValue);

        if (rutValue.length < 8 || !isValidRut(rutValue)) {
            log('needsCompanyConfirmation: RUT invalid or too short');
            return false;
        }

        // Verificar si es RUT de empresa
        const threshold = window.spcbConfig?.companyRutThreshold || 50000000;
        const rutNumber = parseInt(rutValue.slice(0, -1), 10);
        log('needsCompanyConfirmation: RUT number =', rutNumber, 'threshold =', threshold);

        if (!isCompanyRut(rutValue, threshold)) {
            log('needsCompanyConfirmation: not a company RUT');
            return false;
        }

        // Es RUT de empresa + Boleta → necesita confirmación
        log('needsCompanyConfirmation: *** NEEDS CONFIRMATION ***');
        return true;
    }

    /**
     * Mostrar modal de confirmación para RUT de empresa
     * Cuando confirma Boleta, re-dispara el checkout automáticamente
     */
    function showCompanyConfirmationModal() {
        // Remover modal existente si hay
        const existingModal = document.querySelector('.spcb-company-confirm-overlay');
        if (existingModal) existingModal.remove();

        const overlay = document.createElement('div');
        overlay.className = 'spcb-company-confirm-overlay';
        overlay.innerHTML = `
            <div class="spcb-company-confirm-modal">
                <div class="spcb-company-confirm-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="56" height="56">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4">
                        </path>
                    </svg>
                </div>
                <h3 class="spcb-company-confirm-title">RUT de Empresa Detectado</h3>
                <p class="spcb-company-confirm-message">
                    El RUT ingresado corresponde a una <strong>empresa</strong>, pero tienes seleccionado <strong>Boleta</strong>.
                </p>
                <p class="spcb-company-confirm-question">
                    ¿Estás seguro de que deseas recibir una <strong>Boleta</strong> y no una <strong>Factura</strong>?
                </p>
                <div class="spcb-company-confirm-buttons">
                    <button type="button" class="spcb-company-confirm-btn spcb-btn-factura">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        Cambiar a Factura
                    </button>
                    <button type="button" class="spcb-company-confirm-btn spcb-btn-boleta">
                        Sí, quiero Boleta
                    </button>
                </div>
            </div>
        `;

        // Estilos del modal
        if (!document.querySelector('#spcb-company-confirm-styles')) {
            const styles = document.createElement('style');
            styles.id = 'spcb-company-confirm-styles';
            styles.textContent = `
                .spcb-company-confirm-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 999999;
                    animation: spcb-fade-in 0.2s ease;
                    padding: 20px;
                    box-sizing: border-box;
                }
                .spcb-company-confirm-modal {
                    background: white;
                    border-radius: 16px;
                    padding: 32px;
                    max-width: 440px;
                    width: 100%;
                    text-align: center;
                    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.35);
                    animation: spcb-slide-up 0.3s ease;
                }
                .spcb-company-confirm-icon {
                    color: #f59e0b;
                    margin-bottom: 20px;
                    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
                    width: 88px;
                    height: 88px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-left: auto;
                    margin-right: auto;
                }
                .spcb-company-confirm-title {
                    margin: 0 0 16px 0;
                    font-size: 22px;
                    font-weight: 700;
                    color: #1f2937;
                }
                .spcb-company-confirm-message {
                    margin: 0 0 8px 0;
                    font-size: 15px;
                    color: #4b5563;
                    line-height: 1.6;
                }
                .spcb-company-confirm-question {
                    margin: 0 0 28px 0;
                    font-size: 15px;
                    color: #1f2937;
                    line-height: 1.6;
                    font-weight: 500;
                }
                .spcb-company-confirm-buttons {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }
                .spcb-company-confirm-btn {
                    padding: 14px 24px;
                    border-radius: 10px;
                    font-size: 15px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    border: none;
                }
                .spcb-btn-factura {
                    background: linear-gradient(135deg, #008060 0%, #006e52 100%);
                    color: white;
                    box-shadow: 0 4px 14px rgba(0, 128, 96, 0.35);
                }
                .spcb-btn-factura:hover {
                    background: linear-gradient(135deg, #009970 0%, #008060 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(0, 128, 96, 0.4);
                }
                .spcb-btn-boleta {
                    background: #f3f4f6;
                    color: #4b5563;
                    border: 1px solid #e5e7eb;
                }
                .spcb-btn-boleta:hover {
                    background: #e5e7eb;
                    color: #1f2937;
                }
                @media (max-width: 480px) {
                    .spcb-company-confirm-modal {
                        padding: 24px 20px;
                    }
                    .spcb-company-confirm-icon {
                        width: 72px;
                        height: 72px;
                    }
                    .spcb-company-confirm-icon svg {
                        width: 44px;
                        height: 44px;
                    }
                    .spcb-company-confirm-title {
                        font-size: 20px;
                    }
                    .spcb-company-confirm-message,
                    .spcb-company-confirm-question {
                        font-size: 14px;
                    }
                }
            `;
            document.head.appendChild(styles);
        }

        document.body.appendChild(overlay);

        // Manejar botón "Cambiar a Factura"
        const btnFactura = overlay.querySelector('.spcb-btn-factura');
        btnFactura.addEventListener('click', function() {
            overlay.remove();
            log('User chose to switch to Factura');

            // Cambiar a Factura y hacer scroll
            switchToFactura();
            // No re-disparar checkout - el usuario debe completar campos de factura
        });

        // Manejar botón "Sí, quiero Boleta"
        const btnBoleta = overlay.querySelector('.spcb-btn-boleta');
        btnBoleta.addEventListener('click', function() {
            overlay.remove();
            log('User confirmed Boleta for company RUT');

            // Marcar bypass para no volver a preguntar
            setBypass(true);

            // Re-disparar el checkout
            const checkoutButton = document.querySelector('.wc-block-components-checkout-place-order-button');
            if (checkoutButton) {
                log('Re-triggering checkout button click');
                checkoutButton.click();
            }
        });

        // NO cerrar con click en overlay ni con Escape (requiere decisión explícita)
    }

    /**
     * Cambiar el tipo de documento a Factura
     * Hace scroll hacia la sección y resalta visualmente
     */
    function switchToFactura() {
        log('switchToFactura() called');

        // 1. Buscar el radio de factura con varios selectores posibles
        const facturaRadio = document.querySelector(
            'input[name="spcb-document-type-radio"][value="factura"], ' +
            'input[name="spcb-document-type"][value="factura"], ' +
            '#spcb-doc-type-factura'
        );

        if (facturaRadio) {
            // Marcar el radio como checked
            facturaRadio.checked = true;
            // Disparar eventos
            facturaRadio.dispatchEvent(new Event('change', { bubbles: true }));
            facturaRadio.dispatchEvent(new Event('click', { bubbles: true }));
            log('Clicked Factura radio button:', facturaRadio.id);
        }

        // 2. También actualizar el select oculto
        const select = document.querySelector(
            'select[data-spcb-field="document_type"], ' +
            '[data-spcb-field="document_type"] select, ' +
            '#contact-spcb-document_type'
        );

        if (select) {
            select.value = 'factura';
            select.dispatchEvent(new Event('change', { bubbles: true }));
            log('Changed select to factura');
        }

        // 3. Actualizar variable global y body class
        window.spcbDocumentType = 'factura';
        document.body.classList.remove('spcb-boleta-mode');
        document.body.classList.add('spcb-factura-mode');

        // 4. Actualizar cookie
        document.cookie = 'spcb_document_type=factura; path=/; SameSite=Lax';

        // 5. Disparar evento personalizado para que otros scripts reaccionen
        window.dispatchEvent(new CustomEvent('spcb:documentTypeChange', {
            detail: { documentType: 'factura', source: 'company-confirmation-modal' }
        }));

        // 6. Reset bypass ya que cambió a Factura
        setBypass(false);

        log('Document type changed to Factura - global state updated');

        // 7. Scroll hacia la sección de documento/factura
        timers.scheduleTimeout(() => {
            // Buscar la tarjeta de Factura (el contenedor .spcb-radio-card que contiene el radio)
            const facturaCardContainer = facturaRadio?.closest('.spcb-radio-card');
            const facturaLabel = document.querySelector('label[for="spcb-doc-type-factura"]');

            // Buscar el primer campo de factura visible
            const invoiceField = document.querySelector(
                '.wc-block-components-text-input-spcb-invoice_company input, ' +
                '[data-spcb-field="invoice_company"] input, ' +
                'input[id*="invoice_company"]'
            );

            // Hacer scroll al campo de Razón Social si existe, sino a la tarjeta
            const scrollTarget = invoiceField?.closest('.wc-block-components-text-input') ||
                                facturaCardContainer ||
                                facturaLabel;

            if (scrollTarget) {
                scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
                log('Scrolled to:', scrollTarget.className || scrollTarget.id);
            }

            // Resaltar visualmente la tarjeta de Factura
            if (facturaLabel) {
                facturaLabel.style.transition = 'box-shadow 0.3s ease, transform 0.3s ease';
                facturaLabel.style.boxShadow = '0 0 0 3px #008060, 0 4px 20px rgba(0, 128, 96, 0.3)';
                facturaLabel.style.transform = 'scale(1.02)';

                // Quitar resaltado después de 2.5 segundos
                timers.scheduleTimeout(() => {
                    facturaLabel.style.boxShadow = '';
                    facturaLabel.style.transform = '';
                }, 2500, 'remove-factura-highlight');
            }

            // Si hay campo de razón social, hacer focus después del scroll
            if (invoiceField) {
                timers.scheduleTimeout(() => {
                    invoiceField.focus();
                    log('Focused on invoice_company field');
                }, 600, 'focus-invoice-field');
            }

        }, 200, 'scroll-to-factura');
    }

    /**
     * VALIDACIÓN DE CHECKOUT
     * Intercepta el botón de checkout y valida shipping_rut cuando es Factura
     */
    function validateBeforeCheckout() {
        // Obtener tipo de documento
        const docTypeSelect = document.querySelector('[data-spcb-field="document_type"]');
        const docType = docTypeSelect ? docTypeSelect.value : 'boleta';

        log('Checkout validation - document_type:', docType);

        if (docType !== 'factura') {
            return true; // No es factura, permitir
        }

        // Es Factura - verificar shipping_rut
        const shippingRutInput = document.querySelector('input[data-spcb-field="shipping_rut"]');

        if (!shippingRutInput) {
            log('shipping_rut input not found');
            return true; // Campo no existe, permitir
        }

        const shippingRutValue = cleanRut(shippingRutInput.value);
        log('shipping_rut value:', shippingRutValue);

        if (!shippingRutValue || shippingRutValue.length < 8) {
            // Campo vacío o muy corto - BLOQUEAR
            log('BLOCKING: shipping_rut empty for Factura');
            showValidationModal(
                'RUT de quien recibe',
                'Este campo es obligatorio cuando seleccionas Factura. El RUT debe pertenecer a la persona que recibirá el pedido.',
                shippingRutInput
            );
            return false;
        }

        // Validar formato del RUT
        if (!isValidRut(shippingRutValue)) {
            log('BLOCKING: shipping_rut invalid for Factura');
            showValidationModal(
                'RUT de quien recibe',
                'El RUT ingresado no es válido. Verifica que esté correcto.',
                shippingRutInput
            );
            return false;
        }

        // Limpiar error si existía
        clearFieldError(shippingRutInput);

        log('Validation passed');
        return true;
    }

    /**
     * Interceptar clicks en el botón de checkout
     * v1.17.8: Ahora incluye confirmación para RUT empresa + Boleta
     * Estrategia: bloquear síncronamente, mostrar modal, re-disparar si confirma
     * NOTA: La validación de shipping (modal branch/other) se hace en conditional-fields.js
     */
    function interceptCheckoutButton() {
        // Selector del botón de WooCommerce Blocks
        const buttonSelectors = [
            '.wc-block-components-checkout-place-order-button',
            'button[type="submit"].wc-block-components-button',
            '.wp-block-woocommerce-checkout-actions-block button'
        ];

        document.addEventListener('click', function(e) {
            const button = e.target.closest(buttonSelectors.join(', '));

            if (button) {
                log('=== CHECKOUT BUTTON CLICKED ===');
                log('Current bypass status:', getBypass());

                // Primero: verificar si necesita confirmación de RUT empresa + Boleta
                const needsConfirm = needsCompanyConfirmation();
                log('needsCompanyConfirmation() returned:', needsConfirm);

                if (needsConfirm) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    log('*** BLOCKING - Showing company confirmation modal ***');
                    showCompanyConfirmationModal();
                    return false;
                }

                // Segundo: validar campos de Factura si aplica
                if (!validateBeforeCheckout()) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    log('Checkout BLOCKED by RUT validation');
                    return false;
                }

                log('Checkout proceeding normally');
            }
        }, true); // true = capture phase (se ejecuta ANTES que otros handlers)

        log('Checkout button interceptor ready');
    }

    // Resetear el bypass cuando cambie el tipo de documento o el RUT
    function setupBypassReset() {
        // Observar cambios en document type (múltiples selectores para cubrir todos los casos)
        document.addEventListener('change', function(e) {
            if (e.target.matches(
                '[data-spcb-field="document_type"], ' +
                'input[name="spcb-document-type"], ' +
                'input[name="spcb-document-type-radio"], ' +
                '#spcb-doc-type-boleta, ' +
                '#spcb-doc-type-factura'
            )) {
                setBypass(false);
                log('Reset company confirmation bypass (document type changed)');
            }
        });

        // Observar cambios en billing_rut
        document.addEventListener('input', function(e) {
            if (e.target.matches('input[data-spcb-field="billing_rut"]')) {
                setBypass(false);
                log('Reset company confirmation bypass (RUT changed)');
            }
        });
    }

    // Inicializar interceptor después de que el DOM esté listo
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            interceptCheckoutButton();
            setupBypassReset();
        });
    } else {
        interceptCheckoutButton();
        setupBypassReset();
    }

    log('RUT formatter v2.6 ready - with company confirmation modal');

})();
